import {
  Bell01Icon,
  ChevronDownIcon,
  GitBranch01Icon,
} from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { AlertsEditPane } from '@/components/Alerts/AlertsPane';
import { AlertsDataGrid } from '@/components/AlertsDataGrid';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/Command';
import EmptyState from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { Text } from '@/design-system';
import { Button } from '@/design-system/components/Button';
import { useAlerts, useSessions } from '@/hooks/useSwr';
import { AlertRuleWithActions } from '@/types/alerts';
import { SessionSchema } from '@/types/schema';

import { PREBUILT_DASHBOARDS_FILTER } from '../constants';

interface TracingProjectSelectorProps {
  selectedProject?: SessionSchema | null; // undefined for initial load, null for no project selected
  onProjectSelect: (project: SessionSchema) => void;
}

function TracingProjectSelector({
  selectedProject,
  onProjectSelect,
}: TracingProjectSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue] = useDebounce(searchValue, 500, {
    leading: true,
  });

  const sessionsParams = useMemo(() => {
    if (debouncedSearchValue) {
      return {
        name_contains: debouncedSearchValue,
        ...PREBUILT_DASHBOARDS_FILTER,
        sort_by: 'name',
        sort_by_desc: false,
        include_stats: false,
      };
    } else {
      return PREBUILT_DASHBOARDS_FILTER;
    }
  }, [debouncedSearchValue]);

  const { data: sessionsData, isLoading } = useSessions(sessionsParams);

  const handleProjectSelect = (project: SessionSchema) => {
    onProjectSelect(project);
    setOpen(false);
  };

  const rows = sessionsData?.rows || [];

  return (
    <div className="flex items-center gap-2">
      <Text variant="h3" className="font-medium text-tertiary">
        Tracing project
      </Text>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            leftDecorator={GitBranch01Icon}
            rightDecorator={ChevronDownIcon}
            variant="outlined"
            size="md"
            color="secondary"
            className="self-start"
          >
            {selectedProject?.name ??
              (isLoading ? 'Loading...' : 'Select project')}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <Command>
            <CommandInput
              placeholder="Search projects..."
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList className="relative">
              {isLoading && (
                <div className="absolute top-0 w-full">
                  <LinearProgress />
                </div>
              )}
              {rows.length > 0 ? (
                <CommandGroup>
                  {rows.map((project) => (
                    <CommandItem
                      key={project.id}
                      value={project.name}
                      onSelect={() => handleProjectSelect(project)}
                    >
                      <div className="grid grid-cols-[auto_1fr] items-center gap-1">
                        <GitBranch01Icon className="mr-2 h-4 w-4" />

                        <Text>{project.name}</Text>
                        {project.description && (
                          <Text
                            className="col-start-2 line-clamp-3 leading-tight text-tertiary"
                            variant="xs"
                          >
                            {project.description}
                          </Text>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : (
                <CommandEmpty>No results found</CommandEmpty>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export { TracingProjectSelector };

interface TracingProjectAlertsTableProps {
  sessionId: string;
  projectName?: string;
  onCreateAlert: () => void;
  isLoading?: boolean;
}

const TracingProjectAlertsTable = ({
  sessionId,
  projectName,
  onCreateAlert,
  isLoading,
}: TracingProjectAlertsTableProps) => {
  const [selectedAlert, setSelectedAlert] =
    useState<AlertRuleWithActions | null>(null);
  const [isEditAlertPaneOpen, setIsEditAlertPaneOpen] = useState(false);

  const {
    data: alertsData,
    error,
    isValidating,
    mutate,
  } = useAlerts(sessionId);

  const handleEditClick = (alert: AlertRuleWithActions) => {
    setSelectedAlert(alert);
    setIsEditAlertPaneOpen(true);
  };

  if (error) {
    return <ErrorBanner>{error.message}</ErrorBanner>;
  }

  const alerts = alertsData?.session_alert_rules || [];

  return (
    <div className="rounded-md border border-secondary">
      <AlertsDataGrid
        alerts={alerts}
        sessionId={sessionId}
        onEditClick={handleEditClick}
        onDeleteSuccess={mutate}
        isLoading={isLoading || isValidating}
        emptyState={
          <div className="flex h-[70vh] flex-col items-center justify-center py-10">
            <EmptyState
              title="Create your first alert"
              description="Get notified about errors, latency spikes, or changes in feedback scores."
              action={
                <Button size="md" onClick={onCreateAlert}>
                  Create Alert
                </Button>
              }
              fancyIcon={false}
              icon={<Bell01Icon />}
            />
          </div>
        }
      />

      {selectedAlert && (
        <AlertsEditPane
          isOpen={isEditAlertPaneOpen}
          sessionId={sessionId}
          alert={selectedAlert}
          onClose={() => setIsEditAlertPaneOpen(false)}
          projectName={projectName}
        />
      )}
    </div>
  );
};

interface TracingProjectAlertsProps {
  selectedProject?: SessionSchema;
  onProjectSelect: (project: SessionSchema) => void;
  onCreateAlert: () => void;
  isLoading?: boolean;
}

export const TracingProjectAlerts = ({
  selectedProject,
  onProjectSelect,
  onCreateAlert,
  isLoading,
}: TracingProjectAlertsProps) => {
  return (
    <div className="flex flex-col gap-4">
      <TracingProjectSelector
        selectedProject={selectedProject}
        onProjectSelect={onProjectSelect}
      />

      {selectedProject && (
        <TracingProjectAlertsTable
          sessionId={selectedProject.id}
          projectName={selectedProject.name}
          onCreateAlert={onCreateAlert}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};
